import type { EmployeeMedicationDto } from "@/generated/model/employeeMedicationDto";
import { useUpdateEmployeeMedication } from "@/generated/people/people";
import {
  useProfileSectionForm,
  useProfileSectionSave,
} from "@/hooks/use-profile-section-form";
import {
  medicationFormValues,
  medicationRequest,
  medicationSchema,
  showsRegularMedicationDetails,
} from "@/lib/people-profile-sections";

import { ProfileSectionForm } from "../ProfileSectionForm";
import {
  SectionRadioGroup,
  SectionTextArea,
  YES_NO_OPTIONS,
} from "./ProfileSectionFields";

interface EditMedicationFormProps {
  nurseryId: string;
  userId: string;
  medication: EmployeeMedicationDto;
  onDone: () => void;
}

export function EditMedicationForm({
  nurseryId,
  userId,
  medication,
  onDone,
}: EditMedicationFormProps) {
  const { values, setErrors, setField, errors, validate } =
    useProfileSectionForm(
      () => medicationFormValues(medication),
      medicationSchema,
    );
  const update = useUpdateEmployeeMedication({
    mutation: useProfileSectionSave(
      nurseryId,
      userId,
      "Medication details",
      onDone,
      setErrors,
    ),
  });
  const isPending = update.isPending;

  const handleSubmit = () => {
    if (!validate()) return;
    update.mutate({ nurseryId, userId, data: medicationRequest(values) });
  };

  return (
    <ProfileSectionForm
      testId="edit-medication-form"
      onCancel={onDone}
      isPending={isPending}
      onSubmit={handleSubmit}
    >
      <SectionRadioGroup
        label="Takes regular medication"
        value={values.takesRegularMedication}
        options={YES_NO_OPTIONS}
        onChange={(value) => setField("takesRegularMedication", value)}
        disabled={isPending}
      />
      {showsRegularMedicationDetails(values) && (
        <SectionTextArea
          label="Regular medication details"
          name="regularMedicationDetails"
          value={values.regularMedicationDetails}
          onChange={(value) => setField("regularMedicationDetails", value)}
          disabled={isPending}
          error={errors.regularMedicationDetails}
        />
      )}
      <SectionTextArea
        label="Emergency medication details"
        name="emergencyMedicationDetails"
        value={values.emergencyMedicationDetails}
        onChange={(value) => setField("emergencyMedicationDetails", value)}
        disabled={isPending}
        error={errors.emergencyMedicationDetails}
      />
    </ProfileSectionForm>
  );
}
